import { ButtonNavigationBack, ButtonTertiary } from "@nova/component-btn"
import { uploadHotspotKml } from "features/entity-caching/hotspots/thunks"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useState } from "react"
import { zoomToShowHotspotsInFlightPath } from "features/map/zoom/thunks"
import { activateBaseHotspotLayers } from "panels/content/side-panel/thunks"
import { FileExtension } from "@nova/constants"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { WorkspaceTextInput } from "@nova/component-inline-text-input"
import { UploadDropBox } from "panels/content/side-panel/upload-flow/components/UploadDropBox"
import { mapExtensionsToMimeType } from "features/job-management/uploader/constants"
import { InfraredFlight } from "@nova/protobuf"
import { deleteInfraredFlight } from "features/pubsub/entityActions/flights/infrared"
import {
    selectActiveFeatureId,
    selectActiveLayerId,
    setActiveFeatureId,
    setActiveLayerId,
} from "panels/content/side-panel/slice.new"
import { selectEntityById } from "panels/content/side-panel/slice"
import Image from "next/image"

export const KMLUploadContainer = ({ flight }: { flight: InfraredFlight }) => {
    const flightId = flight.id
    const [processing, setProcessing] = useState(false)
    const dispatch = useAppDispatch()

    const { isEditor } = useWorkspacePermissions()

    const [nameInput, setNameInput] = useState(flight.name)

    const activeLayerId = useAppSelector(selectActiveLayerId)
    const entity = useAppSelector(selectEntityById(activeLayerId || ""))
    const activeFeatureId = useAppSelector(selectActiveFeatureId)

    const addFiles = async (files: File[]) => {
        const kml = files[0]
        setProcessing(true)
        const hotspotsResult = await dispatch(uploadHotspotKml({ kml, flightId }))
        if (hotspotsResult.payload === false) {
            setProcessing(false)
            return false
        }
        setProcessing(false)
        zoomTo()
        dispatch(setActiveLayerId({ id: undefined }))
        dispatch(setActiveFeatureId({ feature: undefined }))
    }
    const zoomTo = () => {
        dispatch(zoomToShowHotspotsInFlightPath({ flightId }))
        dispatch(activateBaseHotspotLayers({ flightId }))
    }

    return (
        <>
            <ButtonNavigationBack
                title={"Cancel"}
                onClick={() => {
                    dispatch(deleteInfraredFlight({ id: flightId }))
                    dispatch(setActiveLayerId({ id: undefined }))
                    dispatch(setActiveFeatureId({ feature: undefined }))
                }}
                className="self-start"
            />
            {activeLayerId && activeFeatureId && (
                <div className="flex gap-2 items-center mt-3 mb-4">
                    <div className="h-6 w-6 shrink-0">
                        <Image
                            src="/images/plugins/layers/hotspot.svg"
                            alt="Hotspots"
                            width={24}
                            height={24}
                            className="w-full h-full object-contain"
                            priority
                        />
                    </div>
                    <h4 className="text-gray-0 overflow-hidden whitespace-nowrap text-ellipsis">
                        {`Create ${entity.name}`}
                    </h4>
                </div>
            )}
            <small className="text-gray-100">Layer Name</small>
            <div className="mt-2" />
            <WorkspaceTextInput
                name={"layer-name"}
                value={nameInput}
                handleChange={setNameInput}
                inputClassName="!text-gray-0"
            />
            {isEditor && (
                <div className="mt-4">
                    <UploadDropBox
                        title="Upload File"
                        allowedFileTypes={[FileExtension.kml]}
                        canAcceptMultiple={false}
                        onNewItems={addFiles}
                        isProcessing={processing}
                        supportedMimeTypes={[FileExtension.kml].map(
                            (extension) => mapExtensionsToMimeType[extension]
                        )}
                    />
                </div>
            )}
            <ButtonTertiary
                title="Cancel"
                className="w-full mt-2"
                onClick={() => {
                    dispatch(setActiveLayerId({ id: undefined }))
                    dispatch(setActiveFeatureId({ feature: undefined }))
                }}
            />
        </>
    )
}
